import { createContext, useContext, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronLeft, CircleCheck, Upload, UserPlus } from "lucide-react";
import { api, type S } from "@/shared/api/client";
import { joinList } from "@/shared/i18n";
import { useCan } from "@/shared/lib/session";
import { useImportKinds } from "@/shared/ui/ImportButton";
import { Modal } from "@/shared/ui/Modal";
import { apps, hrefParts, useVisibleApps } from "./apps";
import { useAreas } from "./areas";

/** Opens the dialog for an app just switched on; anything inside the shell may call it. */
const StartContext = createContext<(app: string) => void>(() => {});
export const useAppStart = () => useContext(StartContext);

/** Holds the app just switched on, and shows its dialog over the shell. */
export function AppStartProvider({ children }: { children: ReactNode }) {
  const [app, setApp] = useState<string | null>(null);
  return (
    <StartContext.Provider value={setApp}>
      {children}
      {app && <AppStart app={app} onClose={() => setApp(null)} />}
    </StartContext.Provider>
  );
}

/**
 * An app just switched on: where it sits in the menu, its first steps (ticked as they're
 * done), who'll use it, and its records from Excel; then into the app.
 */
function AppStart({ app, onClose }: { app: string; onClose: () => void }) {
  const { t } = useTranslation();
  const can = useCan();
  const navigate = useNavigate();
  const info = apps.find((a) => a.key === app);
  const opened = useVisibleApps().find((a) => a.key === app);
  const areas = useAreas().filter((a) => a.pages.some((p) => p.app === app));
  const setup = useQuery({ queryKey: ["/v1/core/setup"], queryFn: () => api.get<S["Setup"]>("/v1/core/setup"), enabled: can("core.apps.manage") });
  const steps = (setup.data?.items ?? []).filter((i) => i.app === app);
  const imports = (useImportKinds().data ?? []).filter((k) => k.app === app);
  const name = t(`apps.${app}.name`);
  const open = () => {
    onClose();
    if (opened?.tabs[0]) {
      const { to, search } = hrefParts(opened.tabs[0].to);
      void navigate({ to, search: search as never });
    }
  };
  return (
    <Modal open onClose={onClose} title={t("apps.start.title", { app: name })} size="md"
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>{t("apps.start.later")}</button>
        {opened && <button type="button" className="btn-primary" onClick={open}>{t("apps.start.open", { app: name })}</button>}
      </>}>
      <div className="space-y-5">
        <p className="text-ink-2">{t(`apps.${app}.description`)}</p>
        {areas.length > 0 && <p className="text-[14px]">{t("apps.start.inMenu", { areas: joinList(areas.map((a) => t(`areas.${a.key}`))) })}</p>}
        {steps.length > 0 && (
          <section>
            <h3 className="mb-2 text-[14px] font-semibold">{t("apps.start.steps")}</h3>
            <ol className="space-y-1">
              {steps.map((s) => {
                const to = info?.setup?.[s.key];
                const label = t(`setup.${s.app}.${s.key}`);
                if (s.done || !to) {
                  return (
                    <li key={s.key} className="flex items-start gap-2 px-2 py-1.5">
                      {s.done ? <CircleCheck className="mt-0.5 size-5 shrink-0 text-ledger" /> : <span className="mt-2 size-1.5 shrink-0 rounded-full bg-ink-3" />}
                      <span className={s.done ? "text-ink-3 line-through" : undefined}>{label}</span>
                    </li>
                  );
                }
                const { to: path, search } = hrefParts(to);
                return (
                  <li key={s.key}>
                    <Link to={path} search={search as never} onClick={onClose} className="group flex items-start gap-2 rounded-[var(--radius-ctl)] px-2 py-1.5 hover:bg-paper">
                      <span className="mt-2 size-1.5 shrink-0 rounded-full bg-ledger" />
                      <span className="flex-1 font-medium">{label}</span>
                      <ChevronLeft className="mt-0.5 size-4 shrink-0 text-ink-3 group-hover:text-ledger ltr:rotate-180" />
                    </Link>
                  </li>
                );
              })}
            </ol>
          </section>
        )}
        <div className="flex flex-wrap gap-2">
          {can("core.user.create") && (
            <Link to="/settings/users" search={{ invite: "1" } as never} onClick={onClose} className="btn-secondary">
              <UserPlus className="size-4" /> {t("apps.start.invite")}
            </Link>
          )}
          {imports.length > 0 && (
            <Link to="/settings/import" onClick={onClose} className="btn-secondary">
              <Upload className="size-4" /> {t("apps.start.import")}
            </Link>
          )}
        </div>
      </div>
    </Modal>
  );
}
